// Offline mode (`?offline`): the whole round runs in the browser via the WASM simulation.
// Optional `&players=N` (2–16) adds idle target tanks to test map size and zoom; `&debug` shows
// the debug panel (debug.ts).
// First a free shop: the parts chosen there go into both players' tanks for every round.
// Each player tank wears a random cosmetic of every kind (no victory pose: no match end here).
import { LocalGame, sim_version } from '../pkg/client_wasm.js';
import { el } from './dom.js';
import { KEYMAPS, readInput, takePress } from './input.js';
import { type Item, catalog } from './identity.js';
import { COLOR, Effects, LOOK_SLOTS, POSE } from './looks.js';
import { FLAG_BASE } from './colors.js';
import { type DebugHost, debugPanel } from './debug.js';
import { backToMenu } from './menu.js';
import { type Constants, type Frame, draw, playerColor } from './render.js';
import { renderFreeShop } from './shop.js';
import { countdownSound, sound, soundButton } from './sound.js';
import { FPS_CELL, fpsMeter, keys, statusBar } from './status.js';

const PLAYERS_WITH_KEYS = KEYMAPS.length;

const ROUND_END_DELAY = 3; // seconds between rounds

export function startOffline(ctx: CanvasRenderingContext2D, status: HTMLElement, consts: Constants, dt: number): void {
  const effects = new Effects(); // standard destruction flash; offline has no accounts
  const params = new URLSearchParams(location.search);
  const param = Number(params.get('players'));
  const players = Number.isInteger(param) && param >= 2 && param <= 16 ? param : 2;

  let game: LocalGame;
  let walls: Float32Array;
  let terrain: Float32Array; // ground patches, static for the round
  let prev: Frame;
  let curr: Frame;
  let roundOverAt = 0;
  let loadouts = new Uint8Array(); // 4 bytes per tank with keys
  let looks = new Uint16Array(); // LOOK_SLOTS per tank with keys, until the catalog is in: standard
  catalog().then((items) => (looks = randomLooks(items, Math.min(players, PLAYERS_WITH_KEYS))));
  const [, , , , , setFps, setResult] = statusBar(status, [
    { label: '', width: '130px', content: [el('button', { textContent: 'Leave', onclick: () => backToMenu() })] },
    { label: '', width: '44px', content: [soundButton()] },
    { label: 'Red: move · fire · power-up', width: '250px', content: [...keys('W', 'A', 'S', 'D'), ' ', ...keys('␣'), ' ', ...keys('E', '⇧ L')] },
    { label: 'Blue: move · fire · power-up', width: '300px', content: [...keys('↑', '←', '↓', '→'), ' ', ...keys('⏎'), ' ', ...keys('⇧ R', 'Ctrl R', '⌥ R')] },
    { label: 'New round', width: '100px', content: keys('R') },
    FPS_CELL,
    { label: `Offline v${sim_version()}`, width: 'minmax(220px, 1fr)' },
  ]);
  const countFrame = fpsMeter(setFps);

  const snapshot = (): Frame => ({
    worldWidth: game.world_width(),
    worldHeight: game.world_height(),
    walls,
    barriers: game.barriers(),
    tanks: game.tanks(),
    bullets: game.bullets(),
    zone: game.zone(),
    season: game.season(),
    terrainEffects: game.terrain_effects(),
    terrain,
    holes: game.holes(),
    smoke: game.smoke(),
    wind: Float32Array.from(game.wind()),
    raining: game.raining(),
    fogBanks: game.fog_banks(),
    countdown: game.countdown(),
    pickups: game.pickups(),
    mines: game.mines(), // both players see the same screen
    oil: game.oil(),
    blasts: game.blasts(),
    loadouts,
    looks,
    aimPaths: Array.from({ length: Math.min(players, PLAYERS_WITH_KEYS) }, (_, i) => game.aim_path(i)),
    plainTurrets: true, // Red and Blue stay apart
    players: Math.min(players, PLAYERS_WITH_KEYS),
  });

  /** `season` (`Season::to_u8`, 0 none) and `effects` only from the debug panel; else random. */
  function newRound(season?: number, effects = 0): void {
    game?.free();
    const seed = (Math.random() * 2 ** 32) >>> 0;
    game = season === undefined ? new LocalGame(seed, players, loadouts) : LocalGame.with_season(seed, players, loadouts, season, effects);
    walls = game.walls(); // static for the whole round
    terrain = game.terrain();
    curr = snapshot();
    prev = curr;
    roundOverAt = 0;
    setResult(players > 2 ? `Round on · ${players - 2} target tanks` : 'Round on');
  }

  function checkRoundEnd(now: number): void {
    if (roundOverAt === 0 && game.alive_count() <= 1) {
      roundOverAt = now;
      const w = game.winner();
      sound(w >= 0 ? 'roundWin' : 'roundLose');
      if (w >= 0) setResult(`Player ${w + 1} wins! New round in ${ROUND_END_DELAY} s…`, playerColor(w));
      else setResult(`Draw! New round in ${ROUND_END_DELAY} s…`);
    }
    if (roundOverAt !== 0 && now - roundOverAt > ROUND_END_DELAY * 1000) newRound();
  }

  let lastTime = 0;
  let acc = 0;

  function loop(now: number): void {
    const elapsed = lastTime ? Math.min((now - lastTime) / 1000, 0.25) : 0;
    lastTime = now;
    acc += elapsed * (debug?.speed ?? 1);
    if (debug?.steps) {
      acc += dt * debug.steps; // single steps while paused
      debug.steps = 0;
    }

    if (takePress('KeyR')) newRound();

    while (acc >= dt) {
      for (let i = 0; i < PLAYERS_WITH_KEYS && i < players; i++) {
        const input = readInput(i);
        game.set_input(i, input.throttle, input.turn, input.fire, input.item);
      }
      game.step();
      prev = curr;
      curr = snapshot();
      acc -= dt;
      checkRoundEnd(now);
    }

    countdownSound(curr.countdown ?? 0);
    draw(ctx, { ...interpolate(prev, curr, acc / dt, consts), effects }, consts);
    countFrame(now);
    requestAnimationFrame(loop);
  }

  // Debug panel (`&debug`): speed and single steps act on the loop above.
  const debug: DebugHost | undefined = params.has('debug')
    ? { game: () => game, tanks: () => players, newRound: (s, e) => newRound(s, e), speed: 1, steps: 0 }
    : undefined;

  const shop = document.getElementById('shop')!;
  renderFreeShop(shop, (parts) => {
    loadouts = Uint8Array.from(Array.from({ length: Math.min(players, PLAYERS_WITH_KEYS) }, () => parts).flat());
    shop.hidden = true;
    newRound();
    if (debug) {
      const panel = el('div', { id: 'debug', className: 'panel' });
      document.body.append(panel);
      debugPanel(panel, debug);
    }
    requestAnimationFrame(loop);
  });
  shop.hidden = false;
}

/** For each of `tanks` a random item of every kind in `items` but the pose. */
function randomLooks(items: Item[], tanks: number): Uint16Array<ArrayBuffer> {
  const looks = new Uint16Array(tanks * LOOK_SLOTS);
  for (let t = 0; t < tanks; t++) {
    for (let kind = 0; kind < LOOK_SLOTS; kind++) {
      // Colour looks: only the patterns, no flags.
      const pick = items.filter((i) => i.kind === kind && kind !== POSE && !(kind === COLOR && i.id >= FLAG_BASE));
      if (pick.length) looks[t * LOOK_SLOTS + kind] = pick[Math.floor(Math.random() * pick.length)].id;
    }
  }
  return looks;
}

/** Blends two simulation states so movement is smooth at any frame rate. */
function interpolate(a: Frame, b: Frame, t: number, consts: Constants): Frame {
  const ts = consts.tankStride;
  const tanks = b.tanks.slice();
  if (a.tanks.length === b.tanks.length) {
    for (let o = 0; o < tanks.length; o += ts) {
      tanks[o] = lerp(a.tanks[o], b.tanks[o], t);
      tanks[o + 1] = lerp(a.tanks[o + 1], b.tanks[o + 1], t);
      tanks[o + 2] = lerpAngle(a.tanks[o + 2], b.tanks[o + 2], t);
    }
  }

  const bs = consts.bulletStride;
  const prevPos = new Map<number, number>();
  for (let o = 0; o < a.bullets.length; o += bs) prevPos.set(a.bullets[o], o);
  const bullets = b.bullets.slice();
  for (let o = 0; o < bullets.length; o += bs) {
    const po = prevPos.get(bullets[o]);
    if (po === undefined) continue;
    bullets[o + 1] = lerp(a.bullets[po + 1], b.bullets[o + 1], t);
    bullets[o + 2] = lerp(a.bullets[po + 2], b.bullets[o + 2], t);
  }

  return { ...b, tanks, bullets };
}

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

function lerpAngle(a: number, b: number, t: number): number {
  let d = b - a;
  if (d > Math.PI) d -= Math.PI * 2;
  if (d < -Math.PI) d += Math.PI * 2;
  return a + d * t;
}
